import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { ListChecks, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { getCrecheContractStatuses, createCrecheContractStatus, updateCrecheContractStatus, deleteCrecheContractStatus } from '../../api';

const COLOR_OPTIONS = [
  { value: 'bg-gray-100 text-gray-600', label: 'Gris' },
  { value: 'bg-blue-100 text-blue-700', label: 'Bleu' },
  { value: 'bg-amber-100 text-amber-700', label: 'Amber' },
  { value: 'bg-green-100 text-green-700', label: 'Vert' },
  { value: 'bg-red-100 text-red-600', label: 'Rouge' },
  { value: 'bg-purple-100 text-purple-700', label: 'Violet' },
  { value: 'bg-teal-100 text-teal-700', label: 'Teal' },
  { value: 'bg-orange-100 text-orange-700', label: 'Orange' },
];

const CrecheContractStatusManagement = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ key: '', label: '', color: 'bg-gray-100 text-gray-600' });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ label: '', color: '' });

  const fetchStatuses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getCrecheContractStatuses();
      setStatuses(response.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des statuts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.key.trim() || !formData.label.trim()) return;
    try {
      setLoading(true);
      setError(null);
      await createCrecheContractStatus(formData);
      setFormData({ key: '', label: '', color: 'bg-gray-100 text-gray-600' });
      setShowForm(false);
      fetchStatuses();
    } catch (err) {
      if (err.response?.status === 409) setError('Cette clé existe déjà');
      else { setError("Erreur lors de l'ajout"); console.error(err); }
      setLoading(false);
    }
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setEditData({ label: s.label, color: s.color });
  };

  const cancelEdit = () => { setEditId(null); };

  const saveEdit = async (id) => {
    if (!editData.label.trim()) { cancelEdit(); return; }
    try {
      setLoading(true);
      setError(null);
      await updateCrecheContractStatus(id, editData);
      cancelEdit();
      fetchStatuses();
    } catch (err) {
      setError('Erreur lors de la modification');
      setLoading(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Supprimer le statut "${s.label}" ?\nLes contrats existants garderont cette valeur mais elle ne sera plus proposée.`)) return;
    try {
      setLoading(true);
      setError(null);
      await deleteCrecheContractStatus(s.id);
      fetchStatuses();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
      setLoading(false);
    }
  };

  if (loading && statuses.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <Card><CardContent><p className="text-gray-500 py-8 text-center">Chargement...</p></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListChecks size={20} />
            Statuts de contrat crèche
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">{error}</div>
          )}

          {showForm && (
            <form onSubmit={handleAdd} className="mb-6 p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  type="text"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.key}
                  onChange={(e) => setFormData(f => ({ ...f, key: e.target.value }))}
                  placeholder="Clé unique * (ex: en_cours)"
                  required autoFocus
                />
                <input
                  type="text"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.label}
                  onChange={(e) => setFormData(f => ({ ...f, label: e.target.value }))}
                  placeholder="Libellé * (ex: En cours)"
                  required
                />
                <select
                  value={formData.color}
                  onChange={(e) => setFormData(f => ({ ...f, color: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {COLOR_OPTIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={loading || !formData.key.trim() || !formData.label.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50">
                  <Check size={18} /> Créer
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-md transition">
                  Annuler
                </button>
              </div>
              <p className="text-xs text-gray-500">
                La clé est le slug technique (non modifiable). Le libellé et la couleur sont affichés dans le module Crèche.
              </p>
            </form>
          )}

          <div>
            <h3 className="font-semibold text-gray-700 mb-3">Statuts ({statuses.length})</h3>
            {statuses.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">Aucun statut configuré.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Clé</th>
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Libellé</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Couleur</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Aperçu</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statuses.map((s) => (
                      <tr key={s.id} className="border-t hover:bg-gray-50 transition">
                        <td className="px-3 py-2">
                          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{s.key}</code>
                        </td>
                        <td className="px-3 py-2">
                          {editId === s.id ? (
                            <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                              value={editData.label}
                              onChange={(e) => setEditData(d => ({ ...d, label: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(s.id); if (e.key === 'Escape') cancelEdit(); }}
                              autoFocus />
                          ) : (
                            <span className="text-gray-800 font-medium">{s.label}</span>
                          )}
                        </td>
                        <td className="text-center px-3 py-2">
                          {editId === s.id ? (
                            <select value={editData.color}
                              onChange={(e) => setEditData(d => ({ ...d, color: e.target.value }))}
                              className="text-xs border border-gray-300 rounded px-1 py-1">
                              {COLOR_OPTIONS.map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-xs text-gray-500">{COLOR_OPTIONS.find(c => c.value === s.color)?.label || '?'}</span>
                          )}
                        </td>
                        <td className="text-center px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${editId === s.id ? editData.color : s.color}`}>
                            {editId === s.id ? editData.label : s.label}
                          </span>
                        </td>
                        <td className="text-center px-3 py-2">
                          <div className="flex justify-center gap-1">
                            {editId === s.id ? (
                              <>
                                <button onClick={() => saveEdit(s.id)} className="p-1 text-green-600 hover:bg-green-50 rounded-full" title="Enregistrer">
                                  <Check size={16} />
                                </button>
                                <button onClick={cancelEdit} className="p-1 text-gray-500 hover:bg-gray-200 rounded-full" title="Annuler">
                                  <X size={16} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => startEdit(s)} className="p-1 text-primary hover:bg-primary/5 rounded-full" title="Modifier" disabled={loading}>
                                  <Edit size={16} />
                                </button>
                                <button onClick={() => handleDelete(s)} className="p-1 text-red-600 hover:bg-red-50 rounded-full" title="Supprimer" disabled={loading}>
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {!showForm && (
        <button onClick={() => setShowForm(true)}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Ajouter un statut">
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default CrecheContractStatusManagement;
